import React from "react";
import { Stack, Heading, Text, Box, Button } from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { EICFormData } from "./types";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import { getConnectionTypeLabels } from "./helper";
import ReadOnlySecondaryAccountForPageTwo from "./EICReadOnlySecondaryAccountForPageTwo";
import ReadOnlyBusinessPrimaryForPageTwo from "./EICReadOnlyBusinessPrimaryForPageTwo";

interface EICFormPageTwoReadOnlyProps {
  onBack: () => void;
  onContinue: () => void;
}

const EICFormPageTwoReadOnly: React.FC<EICFormPageTwoReadOnlyProps> = ({
  onBack,
  onContinue,
}) => {
  const { values } = useFormikContext<EICFormData>();

  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    return date.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <Stack space={["xlarge", "xxlarge"]}>
      {/* Content */}
      <Stack>
        <Heading variant="h3">Account details</Heading>
        <Text>
          Let us know who’s going to be responsible for the new account.
        </Text>
      </Stack>
      {/* Content */}

      {/* Request details */}
      <Stack space="large">
        <Heading variant="h4">Premise details</Heading>
        {/* Fields */}
        {/* Premise address */}
        <ReadOnlyTextField label="Premise address" value={values.address} />
        {/* End Premise address */}

        {/* What's being connected */}
        <ReadOnlyTextField
          label="What's being connected?"
          value={getConnectionTypeLabels(values.connectionTypes, true)}
        />

        {/* End What's being connected */}
        {/* End Fields */}
      </Stack>
      {/* End Request details */}

      <Stack space={["large", "xlarge"]}>
        <Heading variant="h4">Account holder</Heading>
        {values.accountType === "personal" && (
          <Text>
            The primary account holder is solely financially liable for the
            account, even if a secondary is also listed.
          </Text>
        )}

        {/* Account holder */}

        <Stack>
          {/* Account type */}
          <ReadOnlyTextField
            label="What type of account holder are you?"
            value={values.accountType === "personal" ? "Personal" : "Business"}
          />
          {/* End Account type */}

          {/* Name/Company name */}
          <ReadOnlyTextField
            label={values.accountType === "personal" ? "Name" : "Company name"}
            value={
              values.accountType === "personal"
                ? `${values.firstName} ${values.lastName}`
                : values.companyName
            }
          />
          {/* End Name/Company name */}

          {values.accountType === "personal" && (
            <Box
              display="flex"
              flexDirection={{
                xs: "column",
                sm: "row",
              }}
            >
              {/* DOB + Phone */}
              {/* Date of birth */}
              <ReadOnlyTextField
                label="Date of birth"
                value={formatDate(values.dateOfBirth)}
              />
              {/* End Date of birth */}
              {/* Phone */}
              <ReadOnlyTextField label="Phone" value={values.phone} />
              {/* End Phone */}
              {/* End DOB + Phone */}
            </Box>
          )}

          {values.accountType === "business" && (
            <ReadOnlyTextField label="ABN" value={values.abn} />
          )}

          {/* Email */}
          {values.accountType === "personal" && (
            <ReadOnlyTextField label="Email" value={values.email} />
          )}
          {/* End Email */}
        </Stack>

        {/* End Account holder */}
      </Stack>
      {/* End What you told us */}

      {/* Secondary Account holder */}
      {values.addSecondaryAccountHolder && (
        <ReadOnlySecondaryAccountForPageTwo
          secondaryFirstName={values.secondaryFirstName}
          secondaryLastName={values.secondaryLastName}
          secondaryDateOfBirth={values.secondaryDateOfBirth}
          secondaryPhone={values.secondaryPhone}
          secondaryEmail={values.secondaryEmail}
          addSecondaryToBills={values.addSecondaryToBills}
          formatDate={formatDate}
        />
      )}
      {/* End Secondary Account holder */}

      {values.accountType === "business" && (
        <ReadOnlyBusinessPrimaryForPageTwo
          firstName={values.firstName}
          lastName={values.lastName}
          dateOfBirth={values.dateOfBirth}
          phone={values.phone}
          email={values.email}
          formatDate={formatDate}
        />
      )}
      {/* End Primary contact */}
      {/* Actions  */}
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: "column", md: "row-reverse" }}
      >
        <Button onClick={onContinue}>Continue</Button>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
      </Box>
      {/* End Actions */}
    </Stack>
  );
};

export default EICFormPageTwoReadOnly;
